import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Loader2, ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

/**
 * Entrada por passagem de sessão (handoff).
 *
 * Outro sistema da plataforma — hoje o InovaOne — pede um token à API e abre
 * esta rota com ele. O token é trocado por uma sessão e descartado.
 *
 * Substitui /auth/:id_conta/:id_usuario, que reautenticava com a senha do
 * usuário obtida por um bearer fixo embutido no bundle público.
 */
export default function EntrarComHandoff() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { entrarComHandoff } = useAuth();

  const [erro, setErro] = useState<string | null>(null);

  // O token serve uma vez só. Sem esta trava, o StrictMode do React em
  // desenvolvimento monta o componente duas vezes e a segunda tentativa
  // queimaria o token, derrubando um login que já tinha dado certo.
  const jaTentou = useRef(false);

  useEffect(() => {
    if (jaTentou.current) return;
    jaTentou.current = true;

    const token = params.get("handoff");
    if (!token) {
      setErro("Link de acesso incompleto.");
      return;
    }

    // Tira o token da barra de endereços antes de qualquer coisa: mesmo já
    // consumido, ele não deve ficar no histórico do navegador nem em print.
    window.history.replaceState(null, "", "/entrar");

    entrarComHandoff(token)
      .then(() => navigate("/", { replace: true }))
      .catch(() => setErro("Este link de acesso expirou ou já foi usado."));
  }, [params, entrarComHandoff, navigate]);

  return (
    <div className="flex min-h-svh items-center justify-center p-6">
      <div className="flex max-w-sm flex-col items-center gap-4 text-center">
        {erro ? (
          <>
            <ShieldAlert className="h-10 w-10 text-destructive" />
            <div>
              <p className="font-medium">Não foi possível entrar</p>
              <p className="text-sm text-muted-foreground">{erro}</p>
            </div>
            <button
              onClick={() => navigate("/auth", { replace: true })}
              className="text-sm underline underline-offset-4"
            >
              Entrar com usuário e senha
            </button>
          </>
        ) : (
          <>
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Entrando…</p>
          </>
        )}
      </div>
    </div>
  );
}
